import { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { useFiles } from '../../hooks/useFiles';
import { useSelection } from '../../hooks/useSelection';

/**
 * Shared drag-drop hook for calendar views
 */
export function useCalendarDrag() {
  const { assignFilesDate } = useFiles();
  const { selectedIndices, selectSingle } = useSelection();
  const [dragOverTarget, setDragOverTarget] = useState(null);

  /**
   * Handle drag start for a file
   */
  const handleFileDragStart = useCallback((e, index) => {
    e.dataTransfer.effectAllowed = 'move';

    if (selectedIndices.has(index)) {
      e.dataTransfer.setData('fileIndices', JSON.stringify(Array.from(selectedIndices)));
    } else {
      selectSingle(index);
      e.dataTransfer.setData('fileIndices', JSON.stringify([index]));
    }
  }, [selectedIndices, selectSingle]);

  /**
   * Handle drag over for monthly view (date cells)
   */
  const handleDragOverDate = useCallback((e, day) => {
    e.preventDefault();
    const dateKey = format(day, 'yyyy-MM-dd');
    setDragOverTarget(dateKey);
  }, []);

  /**
   * Handle drag over for weekly view (time slot cells)
   */
  const handleDragOverTimeSlot = useCallback((e, day, hour) => {
    e.preventDefault();
    const hourKey = format(day, "yyyy-MM-dd") + `T${String(hour).padStart(2, '0')}`;
    setDragOverTarget(hourKey);
  }, []);

  /**
   * Handle drag leave
   */
  const handleDragLeave = useCallback(() => {
    setDragOverTarget(null);
  }, []);

  /**
   * Handle drop for monthly view - assigns at 12:00 noon
   */
  const handleDropToDate = useCallback((e, day) => {
    e.preventDefault();
    setDragOverTarget(null);

    const indicesData = e.dataTransfer.getData('fileIndices');
    if (!indicesData) return;

    try {
      const fileIndices = JSON.parse(indicesData);
      if (!Array.isArray(fileIndices) || fileIndices.length === 0) return;

      const newDate = new Date(day);
      newDate.setHours(12, 0, 0, 0);

      assignFilesDate(fileIndices, newDate);
    } catch (error) {
      console.error('Failed to parse file indices:', error);
    }
  }, [assignFilesDate]);

  /**
   * Handle drop for weekly view - assigns exact time
   */
  const handleDropToTimeSlot = useCallback((e, day, hour, minute = 0) => {
    e.preventDefault();
    setDragOverTarget(null);

    const indicesData = e.dataTransfer.getData('fileIndices');
    if (!indicesData) return;

    try {
      const fileIndices = JSON.parse(indicesData);
      if (!Array.isArray(fileIndices) || fileIndices.length === 0) return;

      const newDate = new Date(day);
      newDate.setHours(hour, minute, 0, 0);

      assignFilesDate(fileIndices, newDate);
    } catch (error) {
      console.error('Failed to parse file indices:', error);
    }
  }, [assignFilesDate]);

  return {
    dragOverTarget,
    handleFileDragStart,
    handleDragOverDate,
    handleDragOverTimeSlot,
    handleDragLeave,
    handleDropToDate,
    handleDropToTimeSlot
  };
}
